import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Typography
} from '@mui/material'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import commonActions from '../../actions/common.actions'
import { parseErrorFromArrayBuffer } from '../common/errorParser'
import { ErrorCodes } from '../../proto/generated/common'
import { initiateDematApplication } from '../../services/apis/tradingAccountProfile'
import { getAccountTabPath } from '../../pages/settings/accountRoutes'
import { blue } from '../../assets/colors'

interface Props {
    open: boolean
    onClose: () => void
    onAccountsUpdated?: () => void
}

const OpenTradingAccountDialog = ({ open, onClose, onAccountsUpdated }: Props) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [dematLoading, setDematLoading] = useState(false)
    const [dematSubmitted, setDematSubmitted] = useState(false)

    const handleGoToPracticeSettings = useCallback(() => {
        onClose()
        navigate(getAccountTabPath('practiceTrade'))
    }, [navigate, onClose])

    const handleOpenDematAccount = useCallback(async () => {
        setDematLoading(true)
        try {
            await initiateDematApplication()
            setDematSubmitted(true)
            onAccountsUpdated?.()
            commonActions.setApiError(dispatch, {
                error: false,
                message: 'Account opening request submitted. We will notify you once your account is activated.',
                errorType: 'success'
            })
        } catch (err: any) {
            const { errorCode, message } = parseErrorFromArrayBuffer(err)
            if (errorCode === ErrorCodes.VALIDATION_ERROR && message?.toLowerCase().includes('pending admin review')) {
                setDematSubmitted(true)
                commonActions.setApiError(dispatch, {
                    error: false,
                    message,
                    errorType: 'info'
                })
                return
            }
            commonActions.setApiError(dispatch, {
                error: true,
                message: message || 'Failed to submit account opening request',
                errorType: 'error'
            })
        } finally {
            setDematLoading(false)
        }
    }, [dispatch, onAccountsUpdated])

    const practiceModeLink = (
        <Link
            component="span"
            role="button"
            tabIndex={0}
            onClick={handleGoToPracticeSettings}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    handleGoToPracticeSettings()
                }
            }}
            underline="hover"
            sx={{
                display: 'inline',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                verticalAlign: 'baseline',
                color: blue,
                fontWeight: 500,
                cursor: dematLoading ? 'default' : 'pointer',
                pointerEvents: dematLoading ? 'none' : 'auto',
                '&:hover': {
                    color: blue,
                },
            }}
        >
            enable practice mode
        </Link>
    )

    return (
        <Dialog open={open} onClose={dematLoading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 500 }}>Start trading</DialogTitle>
            <DialogContent>
                <Typography fontSize="14px" color="text.secondary">
                    {dematSubmitted
                        ? <>Your account opening request is pending review. You can {practiceModeLink} to trade with virtual funds while you wait.</>
                        : <>Open a free Besseggen trading account for live trading, or {practiceModeLink} to trade with virtual margin.</>}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button onClick={onClose} disabled={dematLoading} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                {!dematSubmitted && (
                    <Button
                        variant="contained"
                        onClick={() => void handleOpenDematAccount()}
                        disabled={dematLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        {dematLoading ? <CircularProgress size={18} color="inherit" /> : 'Open free trading account'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default OpenTradingAccountDialog
